import { inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { BehaviorSubject, catchError, map, of, switchMap, tap } from 'rxjs';
import { LoadingState } from '../../shared/types/loading.type';
import { ToastService } from '@ui-lib/components';
import { TranslateService } from '@ngx-translate/core';
import { outputError } from '../../shared/utils/output-error.function';
import { AuditClient } from '../../core/openapi/bff-admin-api/api/audit.client';
import { SearchAuditRequest } from '../../core/openapi/bff-admin-api/model/search-audit-request';
import { HistorySorting } from './history.model';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../shared/const/pagination.constants';

export const HistorySortExpressionParameter: HistorySorting = {
  direction: 0,
  propertyName: 'entityChangedDate',
};

@Injectable()
export class HistoryService {
  private readonly auditClient = inject(AuditClient);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly translateService: TranslateService = inject(TranslateService);

  private readonly historyRequest$ = new BehaviorSubject<SearchAuditRequest>({
    skip: 0,
    take: DEFAULT_TABLE_PAGE_SIZE,
    includeTotalCount: true,
  });

  private readonly _historyTotalCount: WritableSignal<number> = signal(0);
  private readonly _historyLoadingStatus: WritableSignal<LoadingState> = signal(
    LoadingState.LOADING,
  );

  private readonly getHistory$ = this.historyRequest$.pipe(
    tap(() => this._historyLoadingStatus.set(LoadingState.LOADING)),
    switchMap((request) =>
      this.auditClient.v1AuditSearchPost(request).pipe(
        catchError((error) => {
          this.toastService.error({
            message: this.translateService.instant(
              'monitoring.history.toast.history-fetching-failed',
              { error: outputError(error, this.translateService.instant('errors.unknown')) },
            ),
          });

          this._historyTotalCount.set(0);
          return of({ items: [], totalCount: 0 });
        }),
      ),
    ),
    map(({ items, totalCount }) => ({
      items: items ?? [],
      totalCount: totalCount ?? 0,
    })),
    tap(({ totalCount }) => {
      this._historyTotalCount.set(totalCount ?? 0);
      this._historyLoadingStatus.set(LoadingState.READY);
    }),
  );

  public readonly history$ = this.getHistory$.pipe(map(({ items }) => items));

  public get historyTotalCount(): Signal<number> {
    return this._historyTotalCount.asReadonly();
  }

  public get historyLoadingStatus(): Signal<LoadingState> {
    return this._historyLoadingStatus.asReadonly();
  }

  public updateHistory(params: Partial<SearchAuditRequest>): void {
    this.historyRequest$.next({
      ...this.historyRequest$.value,
      ...params,
    });
  }
}
